import { Box, TextField } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import InputLabel from '@components/InputLabel';

import YearInput from './YearInput';
import type { EffectiveDateTimeInputProps } from './types';

const messages = defineMessages({
  dob: {
    id: 'GLOBAL.dob',
    defaultMessage: 'Date of Birth',
  },
});

function determineFormat(inputType: string) {
  let format: string;
  switch (inputType) {
    case 'year':
      format = 'YYYY';
      break;
    case 'month':
      format = 'YYYY-MM';
      break;
    case 'datetime':
      format = 'YYYY-MM-DD HH:mm';
      inputType = 'datetime-local';
      break;
    case 'time':
      format = 'HH:mm';
      break;
    case 'date':
    default:
      format = 'YYYY-MM-DD';
      break;
  }
  return format;
}

/**
 * Input for picking a date + time. Use when you don't want to deal with Date objects and just care about string representations of dates
 */
const EffectiveDateTimeInput: React.FC<EffectiveDateTimeInputProps> = (
  props
) => {
  const {
    label = '',
    error = '',
    value: _initValue,
    onChange,
    onBlur,
    name,
    required = false,
    min,
    max,
  } = props;
  const { formatMessage } = useIntl();
  const maxDate = React.useMemo(() => {
    const isDob =
      name === 'dob' ||
      name === 'dateOfBirth' ||
      label === formatMessage(messages.dob);

    // DOB is date only
    if (isDob && props.type === 'date') {
      return dayjs().format('YYYY-MM-DD');
    }
    return props.max;
  }, [formatMessage, label, name, props.max, props.type]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    let { value } = e.target;
    if (!value) {
      return onChange?.(null);
    }
    if (props.type === 'datetime') {
      value = `${value}:00Z`;
    }
    return onChange?.(value);
  };

  const { dateFormat, inputType, value } = React.useMemo(() => {
    const format = determineFormat(props.type);
    let inputType: string = props.type;
    let value = _initValue;

    if (inputType === 'datetime') {
      inputType = 'datetime-local';
      if (_initValue) {
        value = dayjs(_initValue).utc().format(format);
      }
    }

    return {
      dateFormat: format,
      inputType,
      value,
    };
  }, [props.type, _initValue]);

  if (inputType === 'year') {
    return (
      <YearInput
        {...props}
        min={min ? parseInt(min) : undefined}
        max={max ? parseInt(max) : undefined}
      />
    );
  }

  return (
    <Box sx={{ width: 'inherit' }}>
      <InputLabel label={label} required={required} />
      <TextField
        fullWidth
        error={Boolean(error)}
        helperText={error ? error : null}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={dateFormat}
        name={name}
        value={value ?? ''}
        type={inputType}
        InputProps={{
          inputProps: {
            min,
            max: maxDate,
          },
        }}
        sx={{ minHeight: 47 }}
      />
    </Box>
  );
};

export default EffectiveDateTimeInput;
export type { EffectiveDateTimeInputProps };
